// =============================================================
// Push notification manager (frontend)
// =============================================================
// Handles Service Worker registration, push permission requests,
// subscription management, and communication with the PHP backend.
//
// Usage:
//   import { initPushNotifications, unsubscribePush } from './pushManager';
//   await initPushNotifications();   // after login
//   await unsubscribePush();         // on logout
// =============================================================

import { api } from "./api";

/** Convert a Base64URL-encoded string to a Uint8Array (for applicationServerKey). */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** Cached VAPID public key (fetched once per session). */
let vapidPublicKey: string | null = null;

/**
 * Fetch the VAPID public key from the server.
 * Returns null if push is not configured on the server.
 */
async function getVapidPublicKey(): Promise<string | null> {
  if (vapidPublicKey) return vapidPublicKey;

  try {
    const { data } = await api.get<{ publicKey: string }>(
      "/api/push/vapid-public-key",
    );
    vapidPublicKey = data.publicKey;
    return vapidPublicKey;
  } catch {
    console.warn("[push] VAPID public key not available — push disabled.");
    return null;
  }
}

/**
 * Initialize push notifications:
 * 1. Check browser support
 * 2. Register the Service Worker
 * 3. Request notification permission
 * 4. Subscribe to push (if not already)
 * 5. Send the subscription to the backend
 *
 * Safe to call multiple times — idempotent.
 */
export async function initPushNotifications(): Promise<void> {
  // 1. Check browser support.
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    console.info("[push] Push notifications not supported in this browser.");
    return;
  }

  // 2. Get the VAPID public key from the server.
  const publicKey = await getVapidPublicKey();
  if (!publicKey) return;

  try {
    // 3. Register (or re-use) the Service Worker.
    const registration = await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
    });

    // Wait for the SW to be ready.
    await navigator.serviceWorker.ready;

    // 4. Request notification permission (if not already granted).
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.info("[push] Notification permission denied by user.");
      return;
    }

    // 5. Check for an existing subscription.
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Subscribe to push.
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
      });
    }

    // 6. Send the subscription to the backend.
    await api.post("/api/push/subscribe", {
      subscription: subscription.toJSON(),
    });

    console.info("[push] Push notifications registered successfully.");
  } catch (err) {
    console.error("[push] Failed to initialize push notifications:", err);
  }
}

/**
 * Unsubscribe from push notifications (e.g. on logout).
 * Removes the subscription from both the browser and the backend.
 */
export async function unsubscribePush(): Promise<void> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

  try {
    const registration = await navigator.serviceWorker.getRegistration("/");
    if (!registration) return;

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;

    const endpoint = subscription.endpoint;

    // Unsubscribe from the browser.
    await subscription.unsubscribe();

    // Tell the backend to remove this subscription.
    try {
      await api.post("/api/push/unsubscribe", { endpoint });
    } catch {
      // Best effort — the user is logging out anyway.
    }

    console.info("[push] Push subscription removed.");
  } catch (err) {
    console.error("[push] Failed to unsubscribe:", err);
  }
}
